import { useQuery } from '@apollo/client';
import { getBreadcrumbStateInfo } from 'App/routeHelpers';
import RoutePath from 'App/RoutePath';
import { tracking } from 'App/Tracking';
import { ContentType } from 'Explore/types';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';
import { getToolSummaryFromHtml } from 'utils/getToolSummaryFromHtml';
import { HOME_SECTION_QUERY_OPTIONS } from '../sectionQueryOptions';
import { HomeDailyBoost } from './__generated__/HomeDailyBoost';
import { HOME_DAILY_BOOST_QUERY } from './dailyBoostQuery';
import { useSectionErrorLogging } from './useSectionErrorLogging';

/**
 * Today's Daily Boost short, with the derived copy, route and tracking its two
 * surfaces (the hero banner and the to-do list item) both need.
 *
 * `logSectionErrors` nominates one caller to report a failure. Apollo shares
 * the request between the surfaces but gives each `useQuery` its own
 * `ApolloError`, so logging from the hook unconditionally would raise one
 * Sentry event per mounted surface rather than one per failure. The banner
 * holds it because it is the surface that always renders; if it is ever
 * dropped, the flag has to move with it or the section stops reporting.
 */
export const useDailyBoost = (
  source: string,
  { logSectionErrors = false }: { logSectionErrors?: boolean } = {},
) => {
  const { t: translate } = useTranslation('explore');
  const location = useLocation();
  const breadcrumbState = getBreadcrumbStateInfo(location);

  const { data, loading, error, refetch } = useQuery<HomeDailyBoost>(
    HOME_DAILY_BOOST_QUERY,
    HOME_SECTION_QUERY_OPTIONS,
  );

  useSectionErrorLogging(
    logSectionErrors ? error : undefined,
    data,
    'daily-boost',
  );

  // Gated on `data`: `notifyOnNetworkStatusChange` flips `loading` back to true
  // for a background refetch, and that refetch can fail, neither of which
  // should pull a rendered boost off the page. So `loading` means "nothing to
  // show yet" and `error` "failed with nothing to fall back on".
  const isLoading = loading && !data;
  const sectionError = data ? undefined : error;

  const tool = data?.dailyBoostShort;

  // Both returns spell their keys out rather than spreading a shared object:
  // TypeScript fills a union's missing keys in as optional only for fresh
  // literals, which is what lets a consumer destructure up front and narrow
  // every field on `tool`.
  if (!tool) {
    return { loading: isLoading, error: sectionError, refetch };
  }

  const {
    id,
    slug,
    summary,
    html,
    mediaType,
    estimatedMins,
    exploreCategory,
    isFavourite,
  } = tool;

  const isVideo = mediaType?.value === 'video';
  const toolSummary = (html && getToolSummaryFromHtml(html)) || summary;
  const ctaText = isVideo
    ? translate('featured_hero_banner.short.cta_button.video.text')
    : translate('featured_hero_banner.short.cta_button.audio.text');
  const durationText = translate('featured_hero_banner.short.duration_text', {
    duration: estimatedMins?.toString() ?? '',
  });

  const to = {
    pathname: `${RoutePath.Shorts}/${slug}`,
    state: {
      ...breadcrumbState,
      onToolPlayerClose: {
        location: { pathname: location.pathname },
      },
    },
  };

  const trackShortClick = () => {
    tracking.track('content-selected', {
      contentId: id,
      contentSlug: slug,
      contentDuration: estimatedMins,
      contentType: ContentType.short,
      source,
      contentCategoryPath: exploreCategory?.internalName,
      isFavourite,
    });
  };

  return {
    tool,
    loading: isLoading,
    error: sectionError,
    refetch,
    trackShortClick,
    to,
    ctaText,
    durationText,
    toolSummary,
    isVideo,
  };
};
